import React, { useState, useEffect, useRef } from "react";
import { Upload, message, Button, Progress } from "antd";
import "./UploadClassVideos.css";
import VideoItem from "../VideoItem/VideoItem";
import { createVideoId } from "../../Utilities/videoApi";
import { CloudUploadOutlined, PauseOutlined } from "@ant-design/icons";

const UploadClassVideos = ({ classId, classVideos }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(-1);
  const uploadQueueRef = useRef([]);
  const currentUploadIndexRef = useRef(-1);

  const accessToken = localStorage.getItem("accessToken");
  const storedDeviceId = localStorage.getItem("deviceId");
  const storedUserIp = localStorage.getItem("userIp");

  // Keep refs in sync with state
  useEffect(() => {
    uploadQueueRef.current = uploadQueue;
    currentUploadIndexRef.current = currentUploadIndex;
  }, [uploadQueue, currentUploadIndex]);

  const formatTitle = (str) => {
    if (!str) return str;
    
    return str
      .toLowerCase()
      .split(' ')
      .map(word => {
        if (word.length === 0) return word;
        if (word.includes('-')) {
          return word.split('-')
            .map(subWord => subWord.charAt(0).toUpperCase() + subWord.slice(1))
            .join('-');
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

useEffect(() => {
  console.log("Initializing videos from classVideos", classVideos);

  if (!classVideos?.videos) {
    setVideos([]);
    return;
  }

  const sortedVideos = [...classVideos.videos].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const initialVideos = sortedVideos.map((video, index) => {
    const isUploaded =
      video.hls_status_id === 2 &&
      video.duration > 0;

    const videoId =
      video.video_id ||
      video.id ||
      `temp-${Date.now()}-${index}`;

    return {
      videoId,
      title: video.title || `Video ${index + 1}`,
      file: null, // file must be reselected after refresh

      // 🔑 upload state
      isUploaded,
      uploadStatus: isUploaded ? "completed" : "pending",
      uploadProgress: isUploaded ? 100 : 0,

      // metadata
      isIntro: video.is_intro,
      thumbnail: video.thumbnail_url || video.thumbnail_path || null,
      thumbnailUploaded: !!(video.thumbnail_url || video.thumbnail_path),
      existingVideo: true,
      createdAt: video.created_at || new Date().toISOString(),
      lastFailedChunks: null,
      isProcessed: video.segments_processed, // i have added processing field bolean 

      // optional debug/reference
      originalData: {
        hls_status_id: video.hls_status_id,
        duration: video.duration,
        isProcessed: video.segments_processed,
      }
    };
  });

  console.log("Initialized videos (final):", initialVideos);
  setVideos(initialVideos);
}, [classVideos]);

  const handleInputChange = (videoId, field, value) => {
    console.log(`Updating video ${videoId}: ${field} = ${value}`);
    setVideos(videos.map(video => 
      video.videoId === videoId ? { ...video, [field]: value } : video
    ));
  };

  const handleThumbnailUpload = (videoId, file) => {
    console.log(`Uploading thumbnail for video ${videoId}`);
    setVideos(videos.map(video => {
      if (video.videoId === videoId) {
        if (video.thumbnail instanceof File) {
          URL.revokeObjectURL(video.thumbnail);
        }
        return { 
          ...video, 
          thumbnail: file,
          thumbnailUploaded: false
        };
      }
      return video;
    }));
  };

  const handleRemoveVideo = async (videoId) => {
    setVideos(videos.filter(video => video.videoId !== videoId));
  };

  const handleCreateVideo = async (file) => {
    console.log("Creating new video entry");
    setLoading(true);
    try {
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      const title = formatTitle(fileNameWithoutExtension);
      
      console.log("Creating video ID...");
      const videoId = await createVideoId(
        file,
        classId,
        title,
        videos.length === 0,
        accessToken,
        storedDeviceId,
        storedUserIp
      );
      
      const newVideo = {
        videoId,
        title,
        file,
        isUploaded: false,
        uploadProgress: 0,
        isIntro: videos.length === 0,
        thumbnail: null,
        thumbnailUploaded: false,
        existingVideo: false,
        uploadStatus: 'pending',
        lastFailedChunks: null
      };
      
      setVideos(prevVideos => [...prevVideos, newVideo]);
      message.success('Video added successfully. Please fill in details and upload.');
    } catch (error) {
      console.error('Error handling upload:', error);
      message.error('Failed to add video. ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to start sequential upload
  const handleUploadAllVideos = () => {
    console.log("handleUploadAllVideos called");
    
    // Filter videos that need uploading (including partially uploaded ones)
    const videosToUpload = videos.filter(video => 
      !video.isUploaded && 
       video.file && 
      !video.existingVideo
    );

    console.log("Videos to upload:", videosToUpload);

    if (videosToUpload.length === 0) {
      message.info("No videos to upload. All videos are already uploaded.");
      return;
    }

    // Create upload queue with indices in main videos array
    const queue = videosToUpload.map(video => {
      const indexInVideos = videos.findIndex(v => v.videoId === video.videoId);
      return {
        videoId: video.videoId,
        title: video.title,
        file: video.file,
        indexInVideos: indexInVideos,
        lastFailedChunks: video.lastFailedChunks || null,
        uploadProgress: video.uploadProgress || 0
      };
    });

    console.log("Upload queue created:", queue);
    
    // Set state and refs immediately
    setUploadQueue(queue);
    setCurrentUploadIndex(0);
    setIsUploadingAll(true);
    
    // Update refs immediately (don't wait for useEffect)
    uploadQueueRef.current = queue;
    currentUploadIndexRef.current = 0;
    
    // Start the first upload
    setTimeout(() => {
      startNextUpload();
    }, 0);
  };

  // Function to stop sequential upload
  const handleStopUploadAll = () => {
    setCurrentUploadIndex(-1);
    setIsUploadingAll(false);
    setUploadQueue([]);
    uploadQueueRef.current = [];
    currentUploadIndexRef.current = -1;
    message.info("Upload sequence stopped.");
  };

  // Function to start next upload in sequence
  const startNextUpload = () => {
    console.log("startNextUpload called");
    console.log("Current upload index (ref):", currentUploadIndexRef.current);
    console.log("Upload queue length (ref):", uploadQueueRef.current.length);
    
    if (currentUploadIndexRef.current >= uploadQueueRef.current.length) {
      // All videos uploaded
      console.log("All videos uploaded, finishing sequence");
      setIsUploadingAll(false);
      setCurrentUploadIndex(-1);
      setUploadQueue([]);
      message.success("All videos uploaded successfully!");
      return;
    }

    const currentVideo = uploadQueueRef.current[currentUploadIndexRef.current];
    
    if (currentVideo) {
      console.log(`Starting upload for video: ${currentVideo.title}`);
      // The upload will be triggered by VideoItem's useEffect
    } else {
      console.error("No current video found in queue");
      message.error("No video found in upload queue. Stopping sequence.");
      handleStopUploadAll();
    }
  };

  // Callback when a video upload completes
  const handleVideoUploadComplete = (videoId, success, failedChunks = null) => {
    if (!isUploadingAll) {
      console.log("Not in upload all mode, ignoring completion");
      return;
    }

    console.log(`Video upload complete: ${videoId}, success: ${success}`, failedChunks);

    if (success) {
      // Update video status
      setVideos(prev => prev.map(video => 
        video.videoId === videoId 
          ? { 
              ...video, 
              isUploaded: true, 
              uploadProgress: 100, 
              uploadStatus: 'completed',
              lastFailedChunks: null // Clear failed chunks
            }
          : video
      ));

      // Move to next video
      const nextIndex = currentUploadIndexRef.current + 1;
      console.log(`Moving to next index: ${nextIndex}`);
      setCurrentUploadIndex(nextIndex);
      currentUploadIndexRef.current = nextIndex;
      
      // Start next upload after a short delay
      setTimeout(() => {
        startNextUpload();
      }, 500);
    } else if (failedChunks) {
      // Handle paused upload (not failure)
      console.log(`Video ${videoId} upload paused with ${failedChunks.length} failed chunks`);
      
      // Update video with failed chunks
      setVideos(prev => prev.map(video => 
        video.videoId === videoId 
          ? { 
              ...video, 
              uploadStatus: 'paused',
              lastFailedChunks: failedChunks
            }
          : video
      ));
      
      // Move to next video (skip this one for now)
      const nextIndex = currentUploadIndexRef.current + 1;
      console.log(`Skipping paused video, moving to next index: ${nextIndex}`);
      setCurrentUploadIndex(nextIndex);
      currentUploadIndexRef.current = nextIndex;
      
      // Start next upload after a short delay
      setTimeout(() => {
        startNextUpload();
      }, 500);
    } else {
      // Stop the sequence on actual failure
      console.error(`Upload failed for video ${videoId}. Sequence stopped.`);
      message.error(`Upload failed for video. Sequence stopped.`);
      handleStopUploadAll();
    }
  };

  // Helper function to check if a video is the current upload
  const isVideoCurrentUpload = (videoId) => {
    if (!isUploadingAll || currentUploadIndex < 0 || currentUploadIndex >= uploadQueue.length) {
      return false;
    }
    
    const currentQueueItem = uploadQueue[currentUploadIndex];
    return currentQueueItem && currentQueueItem.videoId === videoId;
  };

  // Helper function to check if upload should be disabled for a video
  const isUploadDisabledForVideo = (video) => {
    if (!isUploadingAll) return false;
    
    // If video is already uploaded, disable upload button
    if (video.isUploaded) return true;
    
    // If video is not in the upload queue, disable upload button
    const isInQueue = uploadQueue.some(item => item.videoId === video.videoId);
    if (!isInQueue) return true;
    
    // If video is in queue but not the current one, disable upload button
    const currentQueueItem = uploadQueue[currentUploadIndex];
    if (currentQueueItem && currentQueueItem.videoId !== video.videoId) {
      return true;
    }
    
    return false;
  };

  return (
    <div className="upload-class-videos-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4 style={{ margin: 0 }}>Upload Class Videos</h4>
        
        {videos.filter(v => !v.isUploaded && v.file).length > 0 && (
          <div>
            {!isUploadingAll ? (
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={handleUploadAllVideos}
                loading={loading}
                disabled={loading}
              >
                Upload All Videos ({videos.filter(v => !v.isUploaded && v.file).length})
              </Button>
            ) : (
              <Button
                type="default"
                danger
                icon={<PauseOutlined />}
                onClick={handleStopUploadAll}
              >
                Stop Upload Sequence
              </Button>
            )}
          </div>
        )}
      </div>

      <p className="upload-class-videos-sum-text">
        If you're teaching your first class on BeyondSchools, we recommend reviewing
        in our Teacher Help Center for a full overview of
        the class creation process.
      </p>

      {isUploadingAll && uploadQueue.length > 0 && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f5ff', borderRadius: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              Uploading video {currentUploadIndex + 1} of {uploadQueue.length}: 
              <strong> {uploadQueue[currentUploadIndex]?.title}</strong>
            </span>
            <Progress
              percent={Math.round(((currentUploadIndex) / uploadQueue.length) * 100)}
              style={{ width: '200px', margin: 0 }}
              size="small"
            />
          </div>
        </div>
      )}

      <div className="upload-class-videos-render">
        <Upload.Dragger
          multiple={true}
          accept="video/*"
          showUploadList={false} 
          className="upload-dragger"
          disabled={loading || isUploadingAll}
          beforeUpload={(file) => {
            handleCreateVideo(file);
            return false;
          }}
        >
          <p className="upload-text">
            {loading ? "Processing..." : 
            (videos.length === 0 ? "Drop your video here, or browse" : `You can add more videos`)}
          </p>
        </Upload.Dragger>

        {videos.length > 0 && (
          <div className="video-list">
            {videos.map((video, index) => {
              const isCurrentUpload = isVideoCurrentUpload(video.videoId);
              const uploadDisabled = isUploadDisabledForVideo(video);
              
              return (
                <VideoItem
                  key={video.videoId}
                  video={video}
                  handleInputChange={handleInputChange}
                  handleRemoveVideo={handleRemoveVideo}
                  handleThumbnailUpload={handleThumbnailUpload}
                  onUploadComplete={handleVideoUploadComplete}
                  isUploadingAll={isUploadingAll}
                  isCurrentUpload={isCurrentUpload}
                  uploadDisabled={uploadDisabled}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadClassVideos;